<template>
  <div class="asc-parameter-graph">
    <asc-spinner-wrapper :loading="loading">
      <div class="asc-parameter-graph--title">
        <asc-subtitle :value="getParameterName"></asc-subtitle>
        <asc-subtitle :value="getDescription"></asc-subtitle>
      </div>
      <div class="asc-parameter-graph--chart" style="margin-left: 0.1rem">
        <v-row no-gutters>
          <v-col sm="12" md="4">
            <template v-if="!isSeriesData">
              <asc-date-picker
                id="parameter-graph-dates"
                key="parameter-graph-dates"
                v-model="savedDates"
                label="Dates"
                :max-days="365"
                :show-fixed-options="true"
                :hide-options-with-id="[6, 7]"
                :default-option-id="dates.defaultOptionId"
                :has-icon="true"
                auto-re-position
                hide-details
                drop-down
                class="parameter-graph-dates"
                @input:id="handleInput"
              ></asc-date-picker>
            </template>
          </v-col>
          <v-col
            v-if="!isEmpty"
            sm="12"
            md="8"
            class="asc-parameter-graph--filter"
          >
            <asc-parameter-graph-filter
              :filters="filters"
              @changes="handleFilterChange"
            />
          </v-col>
        </v-row>
        <asc-stock-chart
          v-if="!isEmpty"
          :options="getChartOptions"
        ></asc-stock-chart>
        <template v-if="!isEmpty && hasValidThresholds">
          <asc-switch v-model="autoscale" label="Autoscale"></asc-switch>
        </template>
      </div>
      <asc-no-data
        v-if="isEmpty"
        class="asc-parameter-graph--no-data"
      ></asc-no-data>
    </asc-spinner-wrapper>
  </div>
</template>

<script>
import { AscDataDefinitionMixin } from '@/components/mixins'
import AscParameterGraphFilter from './AscParameterGraphFilter'
import AscSubtitle from '@/components/atoms/AscSubtitle'
import AscSpinnerWrapper from '@/components/atoms/AscSpinnerWrapper'
import AscSwitch from '@/components/atoms/AscSwitch'
import AscNoData from '@/components/atoms/AscNoData'
import AscDatePicker from '@/components/atoms/AscDatePicker'
import AscStockChart from './AscStockChart'
import {
  getDateTime,
  getFormattedDateTime,
  getReportExportMaxDate,
  getFormattedAPIDate,
} from '@/utilities/dates'
import variables from '@/styles/_varsJs.scss'

export default {
  name: 'AscParameterGraph',

  components: {
    AscDatePicker,
    AscParameterGraphFilter,
    AscSubtitle,
    AscStockChart,
    AscSpinnerWrapper,
    AscSwitch,
    AscNoData,
  },

  mixins: [AscDataDefinitionMixin],

  props: {
    dates: {
      type: Object,
      default: () => {},
    },
    isSeriesData: {
      type: Boolean,
      default: false,
    },
    onDateChange: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      filters: [],
      filterMaxValue: null,
      filterMinValue: null,
      autoscale: false,
      unitSelection: null,
      defaultOptions: {
        chart: {
          pankey: 'ctrl',
          panning: true,
          animation: {
            duration: 1000,
          },
          spacing: 50,
        },
        legend: {
          enabled: true,
        },
        rangeSelector: {
          enabled: true,
          inputEnabled: false,
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 400,
              },
              chartOptions: {
                rangeSelector: { enabled: false },
              },
            },
          ],
        },
        series: [],
        time: {
          useUTC: false,
        },
      },
      savedDates: this.dates,
      maxDate: getReportExportMaxDate(),
    }
  },

  computed: {
    isEmpty() {
      return (
        !this.loading &&
        (this.definition === undefined ||
          this.definition.values === undefined ||
          this.definition.values.length === 0)
      )
    },

    hasValidThresholds() {
      return (
        this.definition &&
        this.definition.thresholds &&
        this.definition.thresholds.length > 0 &&
        (this.definition.thresholds[0].lowAlarm !== 'N/A' ||
          this.definition.thresholds[0].highAlarm !== 'N/A')
      )
    },

    getParameterName() {
      if (this.definition && this.definition.parameterName) {
        return this.definition.parameterName
      }
      return ''
    },

    getDescription() {
      if (this.definition && this.definition.description) {
        return this.definition.description
      }
      return ''
    },

    getChartOptions() {
      if (this.definition && this.definition.values) {
        const xAxisOptions = this.buildXAxisOptions
        const yAxisOptions = this.buildYAxisOptions
        const navigatorOptions = this.buildNavigatorOptions
        const series = this.buildSeries
        const units = this.units
        if (units && units.length) this.refineFilters(units)
        const tooltipOptions = this.buildTooltipOptions

        return {
          ...this.defaultOptions,
          ...series,
          ...xAxisOptions,
          ...yAxisOptions,
          ...navigatorOptions,
          ...tooltipOptions,
          ...units,
        }
      }
      return this.defaultOptions
    },

    buildTooltipOptions() {
      let tooltipOptions = {}
      if (this.definition) {
        if (this.definition.isSeriesData) {
          tooltipOptions = {
            tooltip: {
              formatter: function () {
                return (
                  '<b>' +
                  this.series.name +
                  '</b><br/>' +
                  'Second: ' +
                  this.x +
                  '<br/>' +
                  'Value: ' +
                  this.y
                )
              },
            },
          }
        } else {
          tooltipOptions = {
            tooltip: {
              formatter: function () {
                const pointDate = getFormattedDateTime(this.x)
                return (
                  '<b>' +
                  this.series.name +
                  '</b><br/>' +
                  'Date: ' +
                  pointDate +
                  '<br/>' +
                  'Value: ' +
                  this.y
                )
              },
            },
          }
        }
      }
      return tooltipOptions
    },

    buildXAxisOptions() {
      let xAxisOptions = {}
      if (this.definition) {
        if (this.definition.isSeriesData) {
          xAxisOptions = {
            xAxis: {
              labels: {
                formatter: function () {
                  return this.value
                },
              },
            },
          }
        } else {
          xAxisOptions = {
            xAxis: {},
          }
        }
      }
      return xAxisOptions
    },

    buildNavigatorOptions() {
      let navigatorOptions = {}
      if (this.definition && this.definition.isSeriesData) {
        navigatorOptions = {
          navigator: {
            xAxis: {
              labels: {
                formatter: function () {
                  return this.value
                },
              },
            },
          },
        }
      } else {
        navigatorOptions = {
          navigator: {},
        }
      }
      return navigatorOptions
    },

    buildYAxisOptions() {
      let units = ''
      let maxValue = null
      let minValue = null

      if (
        this.definition &&
        this.definition.thresholds &&
        this.definition.thresholds.length > 0
      ) {
        const first = this.definition.thresholds[0]
        units = first.units
        maxValue = this.autoscale
          ? null
          : this.filterMaxValue > first.highAlarm
          ? this.filterMaxValue
          : first.highAlarm
        minValue = this.autoscale
          ? null
          : this.filterMinValue < first.lowAlarm
          ? this.filterMinValue
          : first.lowAlarm
      }

      const yAxisOptions = {
        yAxis: {
          showLastLabel: true,
          max: null,
          min: null,
          plotLines: this.buildThresholdPlotLines,
          opposite: false,
          title: {
            text: units,
          },
          labels: {
            formatter: function () {
              return Number.isInteger(this.value)
                ? this.value
                : this.value.toFixed(2)
            },
          },
        },
      }

      if (!this.autoscale && !isNaN(maxValue)) {
        yAxisOptions.yAxis.max = Number(maxValue)
      }
      if (!this.autoscale && !isNaN(minValue)) {
        yAxisOptions.yAxis.min = Number(minValue)
      }

      return yAxisOptions
    },

    buildSeries() {
      if (this.definition && this.definition.values) {
        let filteredData = this.definition.values.filter(
          value => !isNaN(value.y),
        )
        if (
          (this.filterMinValue && !this.filterMaxValue) ||
          (this.filterMinValue && this.filterMaxValue < this.filterMinValue)
        ) {
          filteredData = filteredData.filter(
            value => parseFloat(value.y) >= this.filterMinValue,
          )
        } else if (
          this.filterMaxValue &&
          this.filterMaxValue >= this.filterMinValue
        ) {
          filteredData = filteredData.filter(
            value =>
              parseFloat(value.y) >= this.filterMinValue &&
              parseFloat(value.y) <= this.filterMaxValue,
          )
        }
        const chartData = this.definition.isSeriesData
          ? filteredData
          : filteredData.map(point => ({
              x: +getDateTime(point.x),
              y: point.y,
            }))
        const seriesData = {
          series: [
            {
              turboThreshold: 0,
              name: this.definition.parameterName,
              type: 'scatter',
              tooltip: {
                valueDecimals: 0,
              },
              data: chartData,
            },
          ],
        }
        return seriesData
      }
      return []
    },

    buildThresholdPlotLines() {
      const thresholds = []
      if (
        this.definition &&
        this.definition.thresholds &&
        this.definition.thresholds.length > 0
      ) {
        const first = this.definition.thresholds[0]
        const highWarning = first.highAlarm
        if (highWarning !== 'N/A') {
          thresholds.push({
            value: Number(highWarning),
            color: variables.warningbase,
            dashStyle: 'shortdash',
            width: 2,
          })
        }
        const highCaution = first.highWarning
        if (highCaution !== 'N/A') {
          thresholds.push({
            value: Number(highCaution),
            color: variables.cautionbase,
            dashStyle: 'shortdash',
            width: 2,
          })
        }
        const lowCaution = first.lowWarning
        if (lowCaution !== 'N/A') {
          thresholds.push({
            value: Number(lowCaution),
            color: variables.cautionbase,
            dashStyle: 'shortdash',
            width: 2,
          })
        }
        const lowWarning = first.lowAlarm
        if (lowWarning !== 'N/A') {
          thresholds.push({
            value: Number(lowWarning),
            color: variables.warningbase,
            dashStyle: 'shortdash',
            width: 2,
          })
        }
      }

      return thresholds
    },

    units() {
      let nullUnitNumber = 1
      let unitsOnly
      if (this.definition && this.definition.thresholds) {
        unitsOnly = this.definition.thresholds.reduce(
          (acc, threshold, index) => {
            if (!threshold.units || threshold.units === '') {
              threshold.units = `Unit ${nullUnitNumber}`
              nullUnitNumber++
            }
            const findIndex = acc.findIndex(
              item => item.text === threshold.units,
            )
            if (findIndex === -1) {
              acc.push({
                text: threshold.units,
                value: index,
                ...this.getHighestandLowestValue(this.definition.values),
                originalValue: {
                  ...this.getHighestandLowestValue(this.definition.values),
                },
              })
            }
            return acc
          },
          [],
        )
      }
      return unitsOnly
    },
  },

  watch: {
    autoscale(newValue) {
      this.autoScale = newValue
    },
  },

  methods: {
    getHighestandLowestValue() {
      const minAndMax = {
        min: null,
        max: null,
      }
      if (
        this.definition &&
        this.definition.values &&
        this.definition.values.length
      ) {
        const yAxisData = this.definition.values
          .filter(item => !isNaN(item.y))
          .map(item => item.y)
        minAndMax.min = Math.min(...yAxisData)
        minAndMax.max = Math.max(...yAxisData)
      }
      return minAndMax
    },

    handleFilterChange(filter) {
      this.filterMaxValue = filter.max
      this.filterMinValue = filter.min
      this.unitSelection = filter.value
      this.filters[this.unitSelection] = filter
    },

    refineFilters(array) {
      if (!this.filters.length) {
        this.filters = array
      } else {
        this.filters = array
        const updatedFilters = this.filters.reduce(
          (acc, filter, index, arr) => {
            if (filter.text === arr[index].text && index === filter.value) {
              acc.push(filter)
            } else {
              const findIndex = acc.findIndex(item => item.text === filter.text)
              if (findIndex >= 0) {
                filter.value = findIndex
                acc.splice(findIndex, 1, filter)
              } else {
                acc.splice(index, 1)
              }
            }
            return acc
          },
          [],
        )
        this.filters = updatedFilters
      }
    },

    handleInput(id, selection) {
      if (
        selection &&
        selection.dates &&
        selection.dates.startDate &&
        selection.dates.endDate
      ) {
        const oldStartDate = getFormattedAPIDate(
          new Date(this.dates.dates.startDate),
        )
        const oldEndDate = getFormattedAPIDate(
          new Date(this.dates.dates.endDate),
        )
        const oldValue = this.dates.dates.value
        const newStartDate = selection.dates.startDate
        const newEndDate = selection.dates.endDate
        const newValue = selection.dates.value
        if (
          oldStartDate !== newStartDate ||
          oldEndDate !== newEndDate ||
          oldValue !== newValue
        ) {
          if (this.onDateChange) {
            this.onDateChange(selection)
          }
        }
      }
    },
  },
}
</script>

<style lang="scss">
.asc-parameter-graph {
  &--title {
    margin-bottom: 25px;
  }

  &--chart {
    .parameter-graph-dates {
      margin-top: 20px;
      width: 200px;
    }
  }

  &--filter {
    padding-bottom: 4px;
  }

  &--no-data {
    min-height: 400px;
  }

  .asc-filter-label {
    margin-right: 5px;
  }
}
</style>
